import { useMemo } from "react";

import { matchRelatedItem } from "@utils/related-items.util";
import { openNoteMentionInNewTab } from "@utils/note.util";
import { getRelativeTime } from "@utils/date.util";
import { RelatedEntity } from "@app-types/related-items.type";
import { DocumentType } from "@enums/common.enum";
import { MentionNodeAttrs } from "@components/NoteEditor/nodes";

/**
 * Custom hook that handles the click event for a related item.
 *
 * @param item - The related item to handle.
 * @returns An object containing the `handleClick` function.
 */
export function useRelatedItem(item: RelatedEntity) {
  const title = useMemo(() => {
    return (
      matchRelatedItem(item, {
        onDeal: (deal) => `${deal.title} [${deal.stage.title}]`,
        onDocument: (document) => {
          const filename = document.file_name;
          const is_memo = document.document_type === DocumentType.IC_MEMO ? "[IC]" : "";
          const deal = document.deal ? `in "${document.deal.title}" deal` : "";

          return [filename, is_memo, deal].filter(Boolean).join(" ");
        },
        onMeeting: (meeting) => {
          const formattedTime = getRelativeTime(meeting.start_time);
          return `${meeting.title} • ${formattedTime}`;
        },
        onNote: (note) => {
          return note.title ?? "";
        },
        onThread: (thread) => {
          return thread.subject;
        },
      }) ?? ""
    );
  }, [item]);

  const handleClick = (ev: React.MouseEvent<HTMLButtonElement>) => {
    ev.preventDefault();
    ev.stopPropagation();

    openNoteMentionInNewTab(item as MentionNodeAttrs);
  };

  return { handleClick, title };
}
